<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { X, ChevronDown } from 'lucide-vue-next'

interface FilterOption {
  value: string
  label: string
}

const props = withDefaults(defineProps<{
  modelValue: string
  placeholder: string
  options: FilterOption[]
  minWidth?: string
  // false면 "선택 해제(X)"를 지원하지 않는, 항상 값이 있는 드롭다운으로 동작한다
  // (예: 정렬 — 상태/참여여부 같은 선택적 필터와 달리 값이 비어있을 수 없다).
  clearable?: boolean
  fullWidth?: boolean
  // true면 목록을 body로 옮기지 않고 제자리에 그린다. 요소 전체 화면(requestFullscreen) 안에서는
  // body로 옮긴 목록이 보이지 않으므로 그때만 켠다 (예: 이미지 뷰어 전체 화면).
  teleportDisabled?: boolean
}>(), {
  minWidth: '140px',
  clearable: true,
  fullWidth: false,
  teleportDisabled: false,
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const open = ref(false)
const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLDivElement | null>(null)
const dropdownStyle = ref<Record<string, string>>({})

const currentLabel = computed(() => {
  if (!props.modelValue) return props.placeholder
  return props.options.find((o) => o.value === props.modelValue)?.label || props.placeholder
})

const isActive = computed(() => props.clearable && !!props.modelValue)

function computeStyle() {
  const trigger = triggerRef.value
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  const estimatedHeight = 40 * props.options.length + (props.clearable && props.modelValue ? 36 : 0) + 8
  const spaceBelow = window.innerHeight - rect.bottom
  const showUpward = spaceBelow < estimatedHeight && rect.top > estimatedHeight

  dropdownStyle.value = {
    position: 'fixed',
    top: showUpward ? `${rect.top - 4}px` : `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    ...(props.fullWidth ? { width: `${rect.width}px` } : { minWidth: props.minWidth }),
    transform: showUpward ? 'translateY(-100%)' : 'none',
    zIndex: '10001',
  }
}

function toggleOpen() {
  if (open.value) {
    open.value = false
    return
  }
  computeStyle()
  open.value = true
}

function select(value: string) {
  emit('update:modelValue', value)
  open.value = false
}

function clear() {
  emit('update:modelValue', '')
  open.value = false
}

function onOutsideClick(e: MouseEvent) {
  const t = e.target as Node
  if (triggerRef.value?.contains(t)) return
  if (panelRef.value?.contains(t)) return
  open.value = false
}
onMounted(() => document.addEventListener('mousedown', onOutsideClick))
onUnmounted(() => document.removeEventListener('mousedown', onOutsideClick))
</script>

<template>
  <div style="position: relative;">
    <button ref="triggerRef" type="button" @click.stop="toggleOpen" class="flex items-center gap-1" :style="{
      height: '32px', padding: '0 10px', borderRadius: '6px',
      border: open ? '1px solid #6366F1' : isActive ? '1px solid #A5B4FC' : '1px solid #E5E7EB',
      backgroundColor: isActive ? '#EEF2FF' : '#FFFFFF',
      fontSize: '13px', fontWeight: isActive ? 600 : 400,
      color: isActive ? '#4F46E5' : '#374151', cursor: 'pointer',
      whiteSpace: 'nowrap',
      ...(fullWidth
        ? { width: '100%', justifyContent: 'space-between', flexShrink: '1' }
        : { flexShrink: '0' }),
    }">
      {{ currentLabel }}
      <span v-if="clearable && modelValue" @click.stop="clear"
        style="display: flex; align-items: center; color: #6366F1; cursor: pointer;">
        <X :size="11" :stroke-width="2.5" />
      </span>
      <ChevronDown v-else :size="12" :stroke-width="2.5"
        :style="{ color: '#9CA3AF', transform: open ? 'rotate(180deg)' : 'none' }" />
    </button>
    <Teleport to="body" :disabled="teleportDisabled">
      <div v-if="open" ref="panelRef" :style="{
        ...dropdownStyle,
        backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden',
      }">
        <button v-if="clearable && modelValue" type="button" @click="clear" class="flex items-center gap-1"
          style="width: 100%; padding: 8px 12px; font-size: 12px; color: #9CA3AF; background: none; border: none; cursor: pointer; border-bottom: 1px solid #F3F4F6; text-align: left;">
          <X :size="11" :stroke-width="2" /> 선택 해제
        </button>
        <button v-for="opt in options" :key="opt.value" type="button" @click="select(opt.value)"
          class="filter-dropdown-item" :class="{ selected: opt.value === modelValue }" :style="{
          display: 'block', width: '100%', padding: '8px 12px', fontSize: '13px',
          color: opt.value === modelValue ? '#4F46E5' : '#111827',
          fontWeight: opt.value === modelValue ? 600 : 400,
          border: 'none', cursor: 'pointer', textAlign: 'left',
          whiteSpace: 'nowrap',
        }">{{ opt.label }}</button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* 항목 배경은 hover 전환을 위해 인라인 대신 클래스로 지정한다 (인라인 배경은 :hover보다 우선함) */
.filter-dropdown-item {
  background-color: transparent;
  transition: background-color 0.15s ease;
}
.filter-dropdown-item:hover {
  background-color: #F8FAFC;
}
.filter-dropdown-item.selected,
.filter-dropdown-item.selected:hover {
  background-color: #EEF2FF;
}
</style>
